import React from 'react';
import { twMerge } from '@utils/twMerge';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { ResultIcon } from '@dynamika-shared/ui/icons';
import {
  HeaderAttrs,
  OnHeaderCell,
  ReadingTaskRow,
  ReadingTasksTableConfigOptions,
} from './types';
import { getStatusIcon, isDetail } from './readingHelpers';
import { defaultOpts } from './constants';
import { renderDateTime, renderStatus } from '../common/renderers';

const EMPTY_VALUE = '—';

export function buildColumns(
  userOpts?: ReadingTasksTableConfigOptions
): ColumnsType<ReadingTaskRow> {
  const opts = userOpts ? { ...defaultOpts, ...userOpts } : defaultOpts;
  const {
    iconColWidth,
    detailIconIndentPx,
    headerBgClass,
    rowBgClass,
    headerBgInline,
    rowBgInline,
    dataFontSizePx,
    dataLineHeightPx,
    dateTextClass,
    timeTextClass,
    onViewResult,
  } = opts;

  const withHeader = (
    col: ColumnType<ReadingTaskRow>
  ): ColumnType<ReadingTaskRow> => {
    const origOnHeaderCell = col.onHeaderCell;

    return {
      ...col,
      onHeaderCell: (...args: Parameters<OnHeaderCell>): HeaderAttrs => {
        const base: HeaderAttrs = origOnHeaderCell
          ? origOnHeaderCell(...args)
          : {};

        const style = base.style
          ? { ...base.style, background: headerBgInline }
          : { background: headerBgInline };

        return {
          ...base,
          className: twMerge(base.className ?? '', headerBgClass ?? ''),
          style,
        };
      },
    };
  };

  const dataTypographyInline: React.CSSProperties = {
    fontSize: `${dataFontSizePx}px`,
    lineHeight: `${dataLineHeightPx}px`,
  };

  const cellStyle = (
    row: ReadingTaskRow,
    extra?: React.CSSProperties
  ): React.CSSProperties => {
    const base: React.CSSProperties = {
      background: rowBgInline,
      ...dataTypographyInline,
    };

    return extra ? { ...base, ...extra } : base;
  };

  const cellClass = () => rowBgClass || '';

  const getDeletedTextStyle = (
    row: ReadingTaskRow
  ): React.CSSProperties | undefined => {
    if (isDetail(row) && row.deleted) {
      return { textDecoration: 'line-through' };
    }

    return undefined;
  };

  const renderUserCell = (row: ReadingTaskRow) => {
    const secondaryText = isDetail(row)
      ? row.userSecondaryText?.trim() ?? ''
      : '';

    if (!row.user && !secondaryText) {
      return EMPTY_VALUE;
    }

    return (
      <div className='leading-tight min-w-0'>
        <div
          className={twMerge('font-medium truncate', dateTextClass)}
          title={row.user}
        >
          {row.user}
        </div>
        {secondaryText ? (
          <div
            className={twMerge('text-[12px] truncate', timeTextClass)}
            title={secondaryText}
          >
            {secondaryText}
          </div>
        ) : null}
      </div>
    );
  };

  const columns: ColumnsType<ReadingTaskRow> = [
    withHeader({
      title: '',
      key: '__icon',
      width: iconColWidth,
      render: (_: unknown, row: ReadingTaskRow) => {
        const Icon = getStatusIcon(row.status);

        const justifyClass = isDetail(row) ? 'justify-start' : 'justify-center';

        return (
          <span
            className={`inline-flex items-center w-full ${justifyClass} pl-2`}
          >
            <span className='inline-flex items-center text-xl mt-1 [&>svg]:text-yellowGreen'>
              <Icon />
            </span>
          </span>
        );
      },
      onCell: (row: ReadingTaskRow) => ({
        className: cellClass(),
        style: isDetail(row)
          ? cellStyle(row, { paddingLeft: detailIconIndentPx })
          : cellStyle(row),
      }),
    }),

    withHeader({
      title: 'Task Definition',
      key: 'col1',
      width: 270,
      render: (_: unknown, row: ReadingTaskRow) => {
        if (isDetail(row)) {
          const taskType = row.taskType?.trim();
          const readingCode = row.readingCodeText?.trim();

          return (
            <div className='pl-2 leading-tight min-w-0'>
              <div className='font-medium truncate' title={row.taskDefinition}>
                {row.taskDefinition}
              </div>
              {taskType || readingCode ? (
                <div className='flex items-center gap-1 min-w-0 text-[10px] text-dimGray'>
                  {taskType ? (
                    <div className='truncate' title={taskType}>
                      {taskType}
                    </div>
                  ) : null}
                  {readingCode ? (
                    <div className='truncate' title={readingCode}>
                      {readingCode}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        }
        return <span className='font-medium'>{row.taskDefinition}</span>;
      },
      onCell: (row: ReadingTaskRow) => ({
        className: cellClass(),
        style: cellStyle(row, {
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          ...getDeletedTextStyle(row),
        }),
      }),
    }),

    withHeader({
      title: 'User',
      key: 'col2',
      width: 185,
      render: (_: unknown, row: ReadingTaskRow) => renderUserCell(row),
      onCell: (row: ReadingTaskRow) => ({
        className: cellClass(),
        style: cellStyle(row, getDeletedTextStyle(row)),
      }),
    }),

    withHeader({
      title: 'Start Date',
      key: 'col3',
      width: 130,
      render: (_: unknown, row: ReadingTaskRow) =>
        renderDateTime(row.startDate, dateTextClass, timeTextClass),
      onCell: (row: ReadingTaskRow) => ({
        className: cellClass(),
        style: cellStyle(row, getDeletedTextStyle(row)),
      }),
    }),

    withHeader({
      title: 'End Date',
      key: 'col4',
      width: 130,
      render: (_: unknown, row: ReadingTaskRow) =>
        renderDateTime(row.endDate, dateTextClass, timeTextClass),
      onCell: (row: ReadingTaskRow) => ({
        className: cellClass(),
        style: cellStyle(row, getDeletedTextStyle(row)),
      }),
    }),

    withHeader({
      title: 'Status',
      key: 'col5',
      width: 125,
      render: (_: unknown, row: ReadingTaskRow) => renderStatus(row.status),
      onCell: (row: ReadingTaskRow) => ({
        className: cellClass(),
        style: cellStyle(row, getDeletedTextStyle(row)),
      }),
    }),

    withHeader({
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, row: ReadingTaskRow) => {
        const barClass = 'flex w-full items-center justify-start gap-1';

        if (row.type === 'group') {
          return <div className={barClass}>{/* TBD in future */}</div>;
        }

        const hasResult =
          row.resultData !== undefined && row.resultData !== null;

        if (!hasResult) {
          return <div className={barClass} />;
        }

        return (
          <div className={barClass}>
            <button
              type='button'
              aria-label='View result'
              title='Result'
              onClick={e => {
                e.stopPropagation();
                onViewResult?.(row);
              }}
            >
              <ResultIcon className='h-2.5 w-2.5 text-pastelBlue' />
            </button>
          </div>
        );
      },
      onCell: (row: ReadingTaskRow) => ({
        className: cellClass(),
        style: {
          ...cellStyle(row),
          paddingLeft: 10,
          paddingRight: 10,
          textAlign: 'left',
        },
      }),
      onHeaderCell: () => ({
        style: {
          textAlign: 'left',
          paddingLeft: 10,
          paddingRight: 30,
        },
      }),
    }),
  ];

  return columns;
}
